import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, MapPin, ListChecks } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { spacing } from '@/theme'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

export const toArray = (v: string | string[] | null | undefined): string[] => {
  if (!v) return []
  if (Array.isArray(v)) return v
  try {
    const parsed = JSON.parse(v)
    if (Array.isArray(parsed)) return parsed.map(String)
  } catch {
    // legacy single string
  }
  return [v]
}

type InteractionItem = {
  id: string
  title: string
  description: string | null
  image: string | null
  url: string | null
  cities: string | string[] | null
}

export function ModuloItemsScreen() {
  const { moduleKey } = useParams<{ moduleKey: string }>()
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  const [items, setItems] = useState<InteractionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!moduleKey) {
      setLoading(false)
      setError(true)
      return
    }
    api.get(`/interaction-items/active?moduleKey=${encodeURIComponent(moduleKey)}`).then((res) => {
      setItems(res.data.data || [])
    }).catch(() => setError(true)).finally(() => setLoading(false))
  }, [moduleKey])

  const title = moduleKey?.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Contenido'

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(180deg, #020617 0%, #0f172a 30%, #1d4ed8 70%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#93c5fd',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 24,
              padding: spacing[5],
              marginBottom: spacing[4],
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(30, 64, 175, 0.4) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
            }}
          >
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              flexShrink: 0,
            }}>
              <ListChecks size={24} color="#fff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#dbeafe' }}>{title}</h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#93c5fd' }}>Contenido disponible para ti</p>
            </div>
          </motion.div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 20, overflow: 'hidden', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                  <div style={{ height: 140, background: 'rgba(59, 130, 246, 0.05)' }} />
                  <div style={{ padding: spacing[3] }}>
                    <div style={{ width: '70%', height: 16, borderRadius: 8, background: 'rgba(59, 130, 246, 0.1)', marginBottom: 8 }} />
                    <div style={{ width: '100%', height: 10, borderRadius: 8, background: 'rgba(59, 130, 246, 0.05)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <p style={{ fontSize: 15 }}>No hay contenido disponible</p>
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <ListChecks size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>Aún no hay contenido en este módulo</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr', gap: spacing[3] }}>
              {items.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={item.url ? { y: -4, scale: 1.01 } : {}}
                  whileTap={item.url ? { scale: 0.99 } : {}}
                  onClick={() => { if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer') }}
                  style={{
                    borderRadius: 20,
                    overflow: 'hidden',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: item.url ? 'pointer' : 'default',
                  }}
                >
                  {item.image && (
                    <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                      <img
                        src={item.image}
                        alt={item.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, transparent 100%)',
                      }} />
                    </div>
                  )}
                  <div style={{ padding: spacing[3], display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.3 }}>
                      {item.title}
                    </h3>
                    {toArray(item.cities).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {toArray(item.cities).map((c) => (
                          <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#93c5fd', fontWeight: 600 }}>
                            <MapPin size={13} /> {c}
                          </span>
                        ))}
                      </div>
                    )}
                    {item.description && (
                      <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
                        {item.description}
                      </p>
                    )}
                    {item.url && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#3b82f6', fontWeight: 600, marginTop: '4px' }}>
                        <ExternalLink size={13} /> Ver más
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}